import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

export interface DialogConfig {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'confirm' | 'alert';
}

export interface DialogResult {
  confirmed: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private readonly dialogSubject = new Subject<DialogConfig>();
  private readonly resultSubject = new Subject<DialogResult>();

  dialog$ = this.dialogSubject.asObservable();
  result$ = this.resultSubject.asObservable();

  /**
   * Shows a confirmation dialog with OK/Cancel buttons
   * @param message The message to display
   * @param title Optional title for the dialog
   * @returns Observable that emits true if confirmed, false if cancelled
   */
  confirm(message: string, title?: string): Observable<boolean> {
    return new Observable<boolean>(observer => {
      const config: DialogConfig = {
        message,
        title: title ?? 'Confirm',
        confirmText: 'OK',
        cancelText: 'Cancel',
        type: 'confirm',
      };

      this.dialogSubject.next(config);

      const subscription = this.result$.subscribe(result => {
        observer.next(result.confirmed);
        observer.complete();
      });

      return () => {
        subscription.unsubscribe();
      };
    });
  }

  /**
   * Shows an alert dialog with only an OK button
   * @param message The message to display
   * @param title Optional title for the dialog
   * @returns Observable that emits when the dialog is closed
   */
  alert(message: string, title?: string): Observable<void> {
    return new Observable<void>(observer => {
      const config: DialogConfig = {
        message,
        title: title ?? 'Alert',
        confirmText: 'OK',
        type: 'alert',
      };

      this.dialogSubject.next(config);

      const subscription = this.result$.subscribe(() => {
        observer.next();
        observer.complete();
      });

      return () => {
        subscription.unsubscribe();
      };
    });
  }

  /**
   * Called by the dialog component to send the result back
   * @param confirmed Whether the user confirmed or cancelled
   */
  sendResult(confirmed: boolean): void {
    this.resultSubject.next({ confirmed });
  }
}
